import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { formatPhoneBR } from '@/lib/zodSchemas/cnpj';
import { profileSchema, type ProfileInput } from '@/lib/zodSchemas/service';
import { api, ApiError } from '@/lib/api';
import { useToast } from '@/components/ui/toast';
import type { WizardState } from '../wizardState';

interface Props {
  state: WizardState;
  onComplete: (patch: Partial<WizardState>) => void;
}

export function ProfileStep({ state, onComplete }: Props) {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ProfileInput>({
    resolver: zodResolver(profileSchema),
    defaultValues: state.profile
      ? {
          name: state.profile.name,
          crm: state.profile.crm,
          specialty: state.profile.specialty ?? '',
          phone: state.profile.phoneE164.replace(/^\+55/, ''),
        }
      : { name: '', crm: '', specialty: '', phone: '' },
  });

  const toast = useToast();

  const onSubmit = handleSubmit(async (values) => {
    const phoneE164 = `+55${values.phone}`;
    try {
      await api.patch('/users/me', {
        name: values.name,
        crm: values.crm,
        specialty: values.specialty || undefined,
        phoneE164,
      });
      onComplete({
        profile: {
          name: values.name,
          crm: values.crm.toUpperCase(),
          specialty: values.specialty,
          phoneE164,
        },
      });
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : 'Não foi possível salvar seu perfil. Tente novamente.';
      toast.show({ title: message, variant: 'error' });
    }
  });

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
      <div>
        <h2 className="font-display text-2xl font-semibold">Seu perfil</h2>
        <p className="mt-1 text-sm text-(--color-fg-muted)">
          Dados básicos para emissão de NF e alertas fiscais.
        </p>
      </div>

      <FormField id="profile-name" label="Nome completo" error={errors.name?.message} required>
        <Input
          id="profile-name"
          autoComplete="name"
          invalid={!!errors.name}
          {...register('name')}
        />
      </FormField>

      <FormField
        id="profile-crm"
        label="CRM"
        hint="Formato: 12345/UF"
        error={errors.crm?.message}
        required
      >
        <Input
          id="profile-crm"
          placeholder="123456/SP"
          invalid={!!errors.crm}
          {...register('crm')}
        />
      </FormField>

      <FormField id="profile-specialty" label="Especialidade" error={errors.specialty?.message}>
        <Input id="profile-specialty" {...register('specialty')} />
      </FormField>

      <FormField
        id="profile-phone"
        label="Telefone"
        hint="Usado para contato — obrigatório"
        error={errors.phone?.message}
        required
      >
        <Controller
          control={control}
          name="phone"
          render={({ field }) => (
            <Input
              id="profile-phone"
              inputMode="tel"
              autoComplete="tel"
              placeholder="(11) 98765-4321"
              invalid={!!errors.phone}
              value={formatPhoneBR(field.value ?? '')}
              onChange={(e) => field.onChange(e.target.value.replace(/\D/g, ''))}
              onBlur={field.onBlur}
              ref={field.ref}
            />
          )}
        />
      </FormField>

      <Button type="submit" disabled={isSubmitting}>
        {isSubmitting ? 'Salvando…' : 'Continuar'}
      </Button>
    </form>
  );
}
